/**
 * Error message extraction utility for API error responses.
 *
 * This module provides utilities to extract user-friendly error messages from various error shapes,
 * prioritizing the backend's standard error response format:
 * {
 *   "isBadRequest": false,
 *   "errors": [{ "code": "N/A", "message": "User not exist", "details": null }]
 * }
 */

export interface ApiErrorItem {
  code?: string;
  message?: string;
  details?: unknown;
}

export interface ApiErrorResponse {
  isBadRequest?: boolean;
  errors?: ApiErrorItem[];
  message?: string;
  title?: string;
}

/**
 * Narrows an unknown value to a plain object shape so its properties can be probed without `any`.
 */
function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/**
 * Type guard to check if an error has an HTTP response shape (fetch or axios-like).
 */
function hasHttpResponse(error: unknown): error is { response: { data?: unknown; statusText?: string } } {
  return isRecord(error) && isRecord(error.response);
}

/**
 * Normalize an error payload into a flat list of error items. The backend has been observed
 * sending both a bare array (`[{ code, message }]`) and an object wrapper (`{ errors: [...] }`)
 * depending on the endpoint, so both shapes must be accepted.
 */
function normalizeErrorItems(data: unknown): ApiErrorItem[] | undefined {
  if (Array.isArray(data) && data.length > 0) {
    return data as ApiErrorItem[];
  }

  if (isRecord(data) && Array.isArray(data.errors) && data.errors.length > 0) {
    return data.errors as ApiErrorItem[];
  }

  return undefined;
}

/**
 * Extract error messages from API response data.
 * Prioritizes the error items list, then falls back to data.message or data.title.
 *
 * @param data - The API response data (typically from response.data or error.response.data)
 * @returns Array of extracted error messages
 */
function getErrorMessagesFromData(data: unknown): string[] {
  const messages: string[] = [];

  if (!data) {
    return messages;
  }

  // Priority 1: Extract from the error items list (bare array or `errors` wrapper)
  const items = normalizeErrorItems(data);
  if (items) {
    for (const item of items) {
      if (item && typeof item.message === 'string' && item.message.trim().length > 0) {
        messages.push(item.message.trim());
      }
    }
  }

  // Priority 2: Fallback to top-level message or title
  if (messages.length === 0 && isRecord(data)) {
    if (typeof data.message === 'string' && data.message.trim().length > 0) {
      messages.push(data.message.trim());
    } else if (typeof data.title === 'string' && data.title.trim().length > 0) {
      messages.push(data.title.trim());
    }
  }

  return messages;
}

/**
 * Options for error message extraction.
 */
export interface ExtractErrorOptions {
  /** Fallback message if no error message can be extracted. Default: "An unexpected error occurred" */
  fallback?: string;
  /** String used to join multiple error messages. Default: "; " */
  joinWith?: string;
}

/**
 * Extract a user-friendly error message from an unknown error object.
 *
 * This function handles various error shapes:
 * - HTTP errors with response.data (fetch, axios, or custom wrappers)
 * - Standard Error objects with message property
 * - String errors
 * - Unknown/null errors
 *
 * @param error - The error to extract a message from (can be any type)
 * @param options - Configuration options for extraction
 * @returns A user-friendly error message string
 *
 * @example
 * ```typescript
 * try {
 *   await apiService.auth.authLogin({ email, password });
 * } catch (e: unknown) {
 *   const message = extractApiErrorMessage(e, { fallback: "Login failed" });
 *   notifications.error(`User login failed: ${message}`);
 * }
 * ```
 */
export function extractApiErrorMessage(error: unknown, options?: ExtractErrorOptions): string {
  const fallback = options?.fallback || 'An unexpected error occurred';
  const joinWith = options?.joinWith || '; ';

  try {
    if (Array.isArray(error) && error.length > 0) {
      const messages = error
        .map((e) => (isRecord(e) && typeof e.message === 'string' ? e.message.trim() : ''))
        .filter((m) => m.length > 0);

      if (messages.length > 0) return messages.join(joinWith);
    }

    // Existing logic continues here...
    if (hasHttpResponse(error)) {
      const data = error.response.data;
      const messagesFromApi = getErrorMessagesFromData(data);
      if (messagesFromApi.length > 0) return messagesFromApi.join(joinWith);
      if (error.response.statusText) return error.response.statusText;
    }

    if (isRecord(error)) {
      if (error.error) {
        const nestedMessages = getErrorMessagesFromData(error.error);
        if (nestedMessages.length > 0) return nestedMessages.join(joinWith);
      }

      const directMessages = getErrorMessagesFromData(error);
      if (directMessages.length > 0) return directMessages.join(joinWith);

      if (typeof error.message === 'string' && error.message.trim().length > 0) {
        return error.message.trim();
      }
    }

    if (typeof error === 'string' && error.trim().length > 0) {
      return error.trim();
    }

    return fallback;
  } catch {
    return fallback;
  }
}

/**
 * Check if an error is a validation error from the API.
 *
 * @param error - The error to check
 * @returns true if the error is an array of ApiErrorItem objects
 */
export function isValidationError(error: unknown): error is ApiErrorItem[] {
  return Array.isArray(error) && error.length > 0 && 'message' in error[0];
}

/**
 * Extract the first backend error item (code + message + details) from an unknown error object.
 *
 * Only items carrying a non-empty `code` are considered: the legacy validation shape emits
 * `code: "N/A"` placeholders that no caller can act on, but a real code is always meaningful.
 *
 * @param error - The error to extract an item from (can be any type)
 * @returns The first coded error item found, or undefined if none is present
 */
export function extractApiErrorItem(error: unknown): ApiErrorItem | undefined {
  const firstCoded = (items: ApiErrorItem[] | undefined): ApiErrorItem | undefined => {
    const item = items?.[0];
    return item && typeof item.code === 'string' && item.code.trim().length > 0 ? item : undefined;
  };

  try {
    return (
      firstCoded(normalizeErrorItems(error)) ??
      (hasHttpResponse(error) ? firstCoded(normalizeErrorItems(error.response.data)) : undefined) ??
      // Generated API client (HealthSystemApi) throws the HttpResponse with the parsed body on `error`
      (isRecord(error) ? firstCoded(normalizeErrorItems(error.error)) : undefined)
    );
  } catch {
    return undefined;
  }
}

/**
 * Extract the first backend error code (e.g. "hs-user-accounts-2301") from an unknown error object.
 *
 * @param error - The error to extract a code from (can be any type)
 * @returns The first error code found, or undefined if none is present
 */
export function extractApiErrorCode(error: unknown): string | undefined {
  return extractApiErrorItem(error)?.code;
}
